import React, { useState, useEffect, useRef } from 'react'
import * as XLSX from 'xlsx'
import ExcelJS from 'exceljs'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { useLocation, useNavigate } from 'react-router-dom'
import { svaraPaAnsokan } from './InkorgPage'
import { KATEGORI_FARGER, AVDELNING_FARGER, hamtaBemanningskrav } from '../../lib/constants'
import { arHelg } from '../../lib/passLogik'
import { hamtaHelgdagar } from '../../lib/helgdagar'
import { RedigeraPassModal } from '../../components/admin/RedigeraPassModal'

const AVDELNINGAR = ['RN', 'LN MMÖ', 'LN NKP', 'DISP']

// Scrolla tabellen i sidled så att aktuell vecka (måndagen) hamnar först efter namnkolumnen.
// attr = data-attribut på rubrikcellerna, nyckel = funktion som ger attributvärde för ett datum.
function scrollaTillAktuellVecka(scroller, attr, varde, stickySelector) {
  if (!scroller) return
  const nu = new Date()
  const dow = (nu.getDay() + 6) % 7
  const mandag = new Date(nu.getFullYear(), nu.getMonth(), nu.getDate() - dow)
  const kandidater = [varde(mandag), varde(nu)]
  let th = null
  for (const k of kandidater) {
    th = scroller.querySelector(`th[${attr}="${k}"]`)
    if (th) break
  }
  if (!th) return
  const sticky = scroller.querySelector(stickySelector)
  const vanster = th.getBoundingClientRect().left - scroller.getBoundingClientRect().left + scroller.scrollLeft
  scroller.scrollLeft = Math.max(0, vanster - (sticky ? sticky.offsetWidth : 0) - 2)
}

export function SchemaPage() {
  const { medarbetare: adminInfo } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  // Ansökan som admin kom hit från (visas ovanför schemat tills man stänger den, laddar om eller lämnar sidan)
  const [ansokanInfo, setAnsokanInfo] = useState(() => location.state?.ansokan || null)
  const ansokanPeriodVald = useRef(false)
  const [ansokanKommentar, setAnsokanKommentar] = useState(() => location.state?.ansokan?.kommentar_admin || '')
  const [ansokanBusy, setAnsokanBusy] = useState(false)
  // Datum som schemat ska scrolla till (tidigaste datumet i ansökan)
  const ansokanMalDatum = useRef((() => {
    const a = location.state?.ansokan
    if (!a?.fran) return null
    return a.till && a.till < a.fran ? a.till : a.fran
  })())
  useEffect(() => {
    // Rensa navigeringsdatan så bannern försvinner vid omladdning
    if (location.state?.ansokan) navigate(location.pathname, { replace: true, state: null })
  }, [])
  // Admin ser sin egen avdelning först (RN-admin får RN osv)
  const [valdAvdelning, setValdAvdelning] = useState(() =>
    AVDELNINGAR.includes(location.state?.ansokan?.avdelning) ? location.state.ansokan.avdelning
      : AVDELNINGAR.includes(adminInfo?.avdelning) ? adminInfo.avdelning : 'RN')
  const egenAvdValdRef = useRef(AVDELNINGAR.includes(adminInfo?.avdelning) || AVDELNINGAR.includes(location.state?.ansokan?.avdelning))
  useEffect(() => {
    // Om profilen laddades efter första renderingen: välj egen avdelning en gång
    if (!egenAvdValdRef.current && AVDELNINGAR.includes(adminInfo?.avdelning)) {
      egenAvdValdRef.current = true
      setValdAvdelning(adminInfo.avdelning)
      setValdPeriodId(null)
    }
  }, [adminInfo?.avdelning])
  // Vilket val som gäller just nu, så att ett sent svar från en äldre hämtning
  // (t.ex. RN innan profilen laddats och bytt till DISP) inte skriver över den nya.
  const aktuellDataNyckel = useRef('')
  const [medarbetare, setMedarbetare] = useState([])
  const [pass, setPass] = useState([])
  const [passtyper, setPasstyper] = useState([])
  const [dagar, setDagar] = useState([])
  const [valtPass, setValtPass] = useState(null)
  const [loading, setLoading] = useState(true)
  const scrollRef = React.useRef(null)
  const sistScrollatNyckel = React.useRef('')
  const [sparadScroll, setSparadScroll] = useState(0)
  const [allaPerioder, setAllaPerioder] = useState([])
  const [valdPeriodId, setValdPeriodId] = useState(null)
  const [valdPeriod, setValdPeriod] = useState(null)
  async function svaraFranSchema(status) {
    if (!ansokanInfo?.rad || ansokanBusy) return
    setAnsokanBusy(true)
    try {
      await svaraPaAnsokan({ ansokan: ansokanInfo.rad, status, kommentar: ansokanKommentar, admin: adminInfo })
      setAnsokanInfo(prev => prev ? { ...prev, rad: { ...prev.rad, status } } : prev)
      hamtaData() // uppdatera schemat (t.ex. ATF och föräldraledighet läggs in direkt)
    } catch (e) {
      console.error('Kunde inte svara på ansökan:', e)
    } finally {
      setAnsokanBusy(false)
    }
  }

  // Från en ansökan: välj perioden som ansökans datum ligger i (en gång)
  useEffect(() => {
    if (ansokanPeriodVald.current || !ansokanInfo?.fran || allaPerioder.length === 0) return
    const hit = allaPerioder.find(p => p.startdatum <= ansokanInfo.fran && p.slutdatum >= ansokanInfo.fran)
    ansokanPeriodVald.current = true
    if (hit) setValdPeriodId(hit.id)
  }, [allaPerioder])
  const [dagnoteringar, setDagnoteringar] = useState({}) // { datum: text }
  const [redigeraDagNotis, setRedigeraDagNotis] = useState(null) // datum
  const notisOverlayMouseDownPaOverlay = useRef(false)
  const [markerade, setMarkerade] = useState([]) // multimarkering med Ctrl+klick
  const [exporterar, setExporterar] = useState(false)

  useEffect(() => { hamtaPasstyper() }, [])
  useEffect(() => { hamtaData() }, [valdAvdelning, valdPeriodId])
  useEffect(() => { if (valdAvdelning && dagar.length > 0) hamtaDagnoteringar() }, [valdAvdelning, dagar])

  async function hamtaPasstyper() {
    const { data } = await supabase.from('passtyper').select('*').order('namn')
    setPasstyper(data || [])
  }

  async function hamtaDagnoteringar() {
    if (!dagar.length) return
    const { data } = await supabase
      .from('dagnoteringar')
      .select('datum, text')
      .eq('avdelning', valdAvdelning)
      .gte('datum', dagar[0])
      .lte('datum', dagar[dagar.length - 1])
    const map = {}
    for (const d of (data || [])) map[d.datum] = d.text
    setDagnoteringar(map)
  }

  async function sparaDagNotis(datum, text) {
    if (!text.trim()) {
      await supabase.from('dagnoteringar').delete().eq('datum', datum).eq('avdelning', valdAvdelning)
      setDagnoteringar(prev => { const n = {...prev}; delete n[datum]; return n })
    } else {
      const { data: { user } } = await supabase.auth.getUser()
      await supabase.from('dagnoteringar').upsert({ datum, avdelning: valdAvdelning, text: text.trim(), skapad_av: user?.id }, { onConflict: 'datum,avdelning' })
      setDagnoteringar(prev => ({...prev, [datum]: text.trim()}))

      // Skicka en notis till alla aktiva medarbetare i avdelningen
      const adminNamn = adminInfo ? `${adminInfo.fornamn} ${adminInfo.efternamn}` : 'Admin'
      const mottagare = medarbetare.filter(m => m.ar_aktiv !== false)
      await Promise.all(mottagare.map(m => supabase.from('ansokningar').insert({
        typ: 'dagnotering_notis',
        skapad_av: adminInfo?.id || null,
        skapad_av_namn: adminNamn,
        avdelning: m.avdelning,
        fran_datum: datum,
        till_datum: datum,
        status: 'beviljad',
        besvarad_av: adminInfo?.id || null,
        besvarad_av_namn: adminNamn,
        besvarad_at: new Date().toISOString(),
        mottagare_id: m.id,
        admin_kommentar: text.trim()
      })))
    }
    setRedigeraDagNotis(null)
  }

  // Supabase begränsar varje enskild fråga till max 1000 rader på server-
  // sidan, oavsett vad man ber om i .limit(). Den enda pålitliga lösningen
  // är att hämta i omgångar (sidor) om 1000 tills en omgång kommer tillbaka
  // mindre än 1000 - då är man klar. queryBuilder är en FUNKTION som bygger
  // en ny fråga varje gång (måste vara en funktion, inte en färdig fråga,
  // eftersom .range() behöver läggas på varje enskild sida separat).
  async function hamtaAllaSidor(queryBuilder) {
    let allaRader = []
    let sida = 0
    const SIDSTORLEK = 1000
    while (true) {
      // id som sista sortering: utan fast ordning kan rader hoppa över eller dubbleras mellan sidorna
      const { data, error } = await queryBuilder().order('id', { ascending: true }).range(sida * SIDSTORLEK, sida * SIDSTORLEK + SIDSTORLEK - 1)
      if (error) { console.error('Fel vid sidhämtning:', error); break }
      allaRader = allaRader.concat(data || [])
      if (!data || data.length < SIDSTORLEK) break
      sida++
    }
    return allaRader
  }

  async function hamtaData() {
    aktuellDataNyckel.current = `${valdAvdelning}|${valdPeriodId}`
    const dataNyckel = aktuellDataNyckel.current
    setLoading(true)

    // Hämta medarbetare i vald(a) avdelning(ar)
    const { data: med } = await supabase
      .from('medarbetare')
      .select('*')
      .eq('avdelning', valdAvdelning)
      .eq('ar_aktiv', true)
      .order('sort_order', { ascending: true, nullsFirst: false })

    // Hämta alla perioder för avdelningen/avdelningarna
    const { data: perioder } = await supabase
      .from('perioder')
      .select('*')
      .eq('avdelning', valdAvdelning)
      .order('startdatum', { ascending: false })

    if (dataNyckel !== aktuellDataNyckel.current) return // en nyare hämtning har tagit över
    setAllaPerioder(perioder || [])

    // Visa en period i taget - senaste aktiva som default
    const idag = new Date().toISOString().split('T')[0]
    
    // Sortera: aktiva perioder (startdatum nära idag) överst, sedan arkiverade
    const aktivaSorterade = (perioder || [])
      .filter(p => p.slutdatum >= idag)
      .sort((a, b) => a.startdatum.localeCompare(b.startdatum))
    const arkiverade = (perioder || [])
      .filter(p => p.slutdatum < idag)
      .sort((a, b) => b.startdatum.localeCompare(a.startdatum))

    let period = null
    if (valdPeriodId && valdPeriodId !== 'arkiv') {
      period = perioder?.find(p => p.id === valdPeriodId)
    } else if (valdPeriodId === 'arkiv') {
      period = arkiverade[0]
    } else {
      // Default: senaste aktiva period
      period = aktivaSorterade[0] || arkiverade[0]
    }
    const visaPerioder = period ? [period] : []
    setValdPeriod(period || null)
    console.log('Period:', period)
    console.log('Med:', med?.length)
    if (!period || !med?.length) {
      setMedarbetare(med || [])
      setPass([])
      setDagar([])
      setLoading(false)
      return
    }

    // Generera dagar från ALLA valda perioder
    const allaDagarSet = new Set()
    for (const vp of visaPerioder) {
      let d = new Date(vp.startdatum + 'T12:00:00')
      const slut = new Date(vp.slutdatum + 'T12:00:00')
      while (d <= slut) {
        const y = d.getFullYear()
        const m = String(d.getMonth()+1).padStart(2,'0')
        const day = String(d.getDate()).padStart(2,'0')
        allaDagarSet.add(`${y}-${m}-${day}`)
        d.setDate(d.getDate() + 1)
      }
    }
    const dagLista = [...allaDagarSet].sort()
    setDagar(dagLista)

    // Hämta alla pass för alla valda perioder
    const medIds = med.map(m => m.id)
    const forstaDatum = dagLista[0]
    const sistaDatum = dagLista[dagLista.length - 1]
    const passData = await hamtaAllaSidor(() => supabase
      .from('pass')
      .select('*, passtyp:passtyper(*), har_passandring')
      .in('medarbetare_id', medIds)
      .gte('datum', forstaDatum)
      .lte('datum', sistaDatum)
    )
    // OBS: Supabase begränsar varje enskild fråga till max 1000 rader på
    // serversidan (går INTE att kringgå med .limit()) - därför hämtas i
    // sidor ovan istället, annars faller pass bort helt tyst.

    if (dataNyckel !== aktuellDataNyckel.current) return // en nyare hämtning har tagit över
    setMedarbetare(med)
    setPass(passData || [])
    setLoading(false)
    // Återställ scrollposition
    setTimeout(() => {
      if (scrollRef.current && sparadScroll > 0) {
        scrollRef.current.scrollLeft = sparadScroll
      } else if (scrollRef.current && ansokanMalDatum.current) {
        // Kommer från en ansökan: scrolla till ansökans datum (så snart perioden med datumet visas)
        const th = scrollRef.current.querySelector(`th[data-datum="${ansokanMalDatum.current}"]`)
        if (th) {
          const sticky = scrollRef.current.querySelector('th.sticky-col')
          const vanster = th.getBoundingClientRect().left - scrollRef.current.getBoundingClientRect().left + scrollRef.current.scrollLeft
          scrollRef.current.scrollLeft = Math.max(0, vanster - (sticky ? sticky.offsetWidth : 0) - 2)
          ansokanMalDatum.current = null
        }
      } else if (scrollRef.current && sistScrollatNyckel.current !== `${valdAvdelning}|${valdPeriodId}`) {
        // Ny avdelning/period: hamna på aktuell vecka
        sistScrollatNyckel.current = `${valdAvdelning}|${valdPeriodId}`
        const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
        scrollaTillAktuellVecka(scrollRef.current, 'data-datum', fmt, 'th.sticky-col')
      }
    }, 50)
  }

  async function exporteraBackup() {
    setExporterar(true)
    try {
      // Hämta ALL data – alla avdelningar, alla medarbetare, alla pass, all historik och alla dagnoteringar
      const { data: allaMedarbetare } = await supabase
        .from('medarbetare').select('*').order('avdelning').order('fornamn')

      const allaPass = await hamtaAllaSidor(() => supabase
        .from('pass')
        .select('*, medarbetare:medarbetare(fornamn, efternamn, avdelning), passtyp:passtyper(namn, starttid, sluttid)')
        .order('datum')
      )

      const allHistorik = await hamtaAllaSidor(() => supabase
        .from('pass_historik').select('*').order('andrad_at')
      )

      const { data: allaDagnoteringar } = await supabase
        .from('dagnoteringar').select('*')

      // Gruppera historik per pass_id för snabb uppslagning
      const historikPerPass = {}
      for (const h of allHistorik || []) {
        if (!historikPerPass[h.pass_id]) historikPerPass[h.pass_id] = []
        historikPerPass[h.pass_id].push(h)
      }

      // Gruppera dagnoteringar per (avdelning + datum)
      const dagnotisMap = {}
      for (const d of allaDagnoteringar || []) {
        dagnotisMap[`${d.avdelning}_${d.datum}`] = d.text || ''
      }

      const wb = XLSX.utils.book_new()

      // En flik per avdelning med alla pass
      for (const avd of AVDELNINGAR) {
        const passIAvd = (allaPass || []).filter(p => p.medarbetare?.avdelning === avd)
        const rader = passIAvd.map(p => {
          const historikTxt = (historikPerPass[p.id] || [])
            .map(h => `${new Date(h.andrad_at).toLocaleString('sv-SE')} (${h.andrad_av_namn}): ${h.gammal_passtyp} → ${h.ny_passtyp}${h.notering ? ' – ' + h.notering : ''}`)
            .join(' | ')
          return {
            Datum: p.datum,
            Medarbetare: `${p.medarbetare?.fornamn || ''} ${p.medarbetare?.efternamn || ''}`.trim(),
            Passtyp: p.passtyp_namn || '',
            Kategori: p.kategori || '',
            'Tid': p.passtyp ? `${p.passtyp.starttid?.substring(0,5)}–${p.passtyp.sluttid?.substring(0,5)}` : '',
            Timmar: p.arbetstimmar ?? '',
            'ATF från': p.har_atf ? (p.atf_fran?.substring(0,5) || '') : '',
            'ATF till': p.har_atf ? (p.atf_till?.substring(0,5) || '') : '',
            Dagnotering: dagnotisMap[`${avd}_${p.datum}`] || '',
            Ändringslogg: historikTxt
          }
        })
        const ws = XLSX.utils.json_to_sheet(rader)
        ws['!cols'] = [{wch:11},{wch:22},{wch:10},{wch:16},{wch:13},{wch:8},{wch:9},{wch:9},{wch:30},{wch:60}]
        XLSX.utils.book_append_sheet(wb, ws, avd.substring(0, 31))
      }

      // Extra flik: alla medarbetare (för återställning om något krånglar)
      const medRader = (allaMedarbetare || []).map(m => ({
        Förnamn: m.fornamn, Efternamn: m.efternamn, KID: m.kid,
        Anställningsnummer: m.anstallningsnummer || '', 'E-post': m.email,
        Avdelning: m.avdelning, Schematyp: m.schema_typ,
        Admin: m.ar_admin ? 'Ja' : 'Nej', Aktiv: m.ar_aktiv !== false ? 'Ja' : 'Nej'
      }))
      const wsMed = XLSX.utils.json_to_sheet(medRader)
      XLSX.utils.book_append_sheet(wb, wsMed, 'Medarbetare')

      const datumTxt = new Date().toISOString().split('T')[0]
      XLSX.writeFile(wb, `EON_Schema_backup_${datumTxt}.xlsx`)
    } catch (err) {
      console.error('Export-fel:', err)
      alert('Kunde inte exportera: ' + (err?.message || 'Okänt fel'))
    } finally {
      setExporterar(false)
    }
  }

  function hexTillArgb(hex) {
    if (!hex || hex === 'transparent') return null
    let h = hex.replace('#', '').toUpperCase()
    if (h.length === 3) h = h.split('').map(c => c + c).join('') // t.ex. "444" → "444444"
    return 'FF' + h
  }

  async function exporteraPlaneringsunderlag() {
    // Hämta ändringshistorik för alla pass i vald period (till anteckningar i rutorna)
    const historikPerPass = {}
    try {
      const passIds = pass.map(p => p.id).filter(Boolean)
      for (let i = 0; i < passIds.length; i += 150) {
        const delIds = passIds.slice(i, i + 150)
        const rader = await hamtaAllaSidor(() => supabase
          .from('pass_historik').select('*').in('pass_id', delIds).order('andrad_at'))
        for (const h of rader || []) {
          if (!historikPerPass[h.pass_id]) historikPerPass[h.pass_id] = []
          historikPerPass[h.pass_id].push(h)
        }
      }
    } catch (e) { console.error('Kunde inte hämta historik till Excel:', e) }

    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet(valdAvdelning.substring(0, 31), {
      views: [{ state: 'frozen', xSplit: 1, ySplit: 2 }]
    })

    // Helgdagar (röda dagar) för alla år som perioden berör
    const helgdagsNamn = {}
    for (const ar of new Set(dagar.map(d => d.substring(0, 4)))) {
      Object.assign(helgdagsNamn, hamtaHelgdagar(Number(ar)))
    }
    const arLedigDag = d => {
      const dow = new Date(d + 'T12:00:00').getDay()
      return dow === 0 || dow === 6 || !!helgdagsNamn[d]
    }

    // Kolumner: namn + en per dag + summa
    ws.columns = [
      { header: 'Medarbetare', width: 24 },
      ...dagar.map(d => {
        const dt = new Date(d + 'T12:00:00')
        const rubrik = `${dt.toLocaleDateString('sv-SE', { weekday: 'short' })} ${dt.getDate()}/${dt.getMonth()+1}`
        return { header: helgdagsNamn[d] ? `${rubrik}\n${helgdagsNamn[d]}` : rubrik, width: 8 }
      }),
      { header: 'Summa', width: 10 }
    ]

    // Rad ovanför datumrubrikerna: dagsnoteringar (gäller hela dagen)
    const noteringsTexter = dagar.map(d => dagnoteringar[d] || '')
    ws.spliceRows(1, 0, ['Dagsnotering', ...noteringsTexter, ''])
    const noteringsRad = ws.getRow(1)
    const langsta = Math.max(0, ...noteringsTexter.map(t => t.length))
    noteringsRad.height = langsta > 0 ? Math.min(150, Math.max(22, Math.ceil(langsta / 7) * 12 + 6)) : 22
    noteringsRad.eachCell({ includeEmpty: true }, (cell, colNr) => {
      cell.alignment = { horizontal: colNr === 1 ? 'left' : 'center', vertical: 'top', wrapText: true }
      cell.font = { bold: colNr === 1, size: 8, color: { argb: colNr === 1 ? 'FF444444' : 'FF2E7D32' } }
      if (colNr > 1 && colNr <= dagar.length + 1 && cell.value) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } }
      }
      cell.border = { bottom: { style: 'thin', color: { argb: 'FFDDDDDD' } } }
    })

    // Rubrikrad – Eon-röd bakgrund, vit fet text
    const rubrikRad = ws.getRow(2)
    rubrikRad.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF11C0A' } }
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
      cell.border = { bottom: { style: 'thin', color: { argb: 'FFDDDDDD' } } }
    })
    // Helgdagsnamn i mindre text under datumet så hela namnet ryms
    dagar.forEach((d, i) => {
      if (!helgdagsNamn[d]) return
      const dt = new Date(d + 'T12:00:00')
      const rubrik = `${dt.toLocaleDateString('sv-SE', { weekday: 'short' })} ${dt.getDate()}/${dt.getMonth()+1}`
      rubrikRad.getCell(i + 2).value = { richText: [
        { text: rubrik + '\n', font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' } } },
        { text: helgdagsNamn[d], font: { bold: false, size: 7, color: { argb: 'FFFFFFFF' } } }
      ] }
    })
    rubrikRad.height = dagar.some(d => helgdagsNamn[d]) ? 62 : 22

    // Datarader – en per medarbetare, en cell per dag, färgkodad efter kategori
    medarbetare.forEach(med => {
      const radData = [`${med.fornamn} ${med.efternamn}`]
      for (const datum of dagar) {
        const p = getPass(med.id, datum)
        radData.push(p?.passtyp_namn || '')
      }
      radData.push(getTotalTimmar(med.id))
      const rad = ws.addRow(radData)

      rad.getCell(1).font = { bold: true }
      rad.getCell(1).alignment = { vertical: 'middle' }

      let harKommentar = false
      dagar.forEach((datum, i) => {
        const p = getPass(med.id, datum)
        const cell = rad.getCell(i + 2) // +2: kolumn 1 är namn
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFEEEEEE' } },
          bottom: { style: 'thin', color: { argb: 'FFEEEEEE' } },
          left: { style: 'thin', color: { argb: 'FFEEEEEE' } },
          right: { style: 'thin', color: { argb: 'FFEEEEEE' } },
        }
        // Helg/röd dag utan pass = grå ruta
        if ((!p || !p.passtyp_namn) && arLedigDag(datum)) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } }
        }
        if (p) {
          // Beredskap = röd ruta, boka dag = blå ruta (samma färger som färgnyckeln), annars passets kategori
          const farg = p.har_beredskap ? KATEGORI_FARGER.beredskap
            : p.har_boka_dag ? KATEGORI_FARGER.boka_dag
            : KATEGORI_FARGER[p.kategori]
          const bg = hexTillArgb(farg?.bg)
          if (bg) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
          const textFarg = hexTillArgb(farg?.text)
          if (textFarg) cell.font = { color: { argb: textFarg }, bold: !!(p.har_beredskap || p.har_boka_dag) }

          // Beviljad semester = tjock grön kant
          if (p.kategori === 'beviljad_semester') {
            const gron = { style: 'thick', color: { argb: 'FF2E7D32' } }
            cell.border = { top: gron, bottom: gron, left: gron, right: gron }
          }

          // Text under passnamnet: timmar för X-dagar och/eller kommentar (t.ex. "D Rakel")
          const xTimmar = ['X','SX','LX','SJX'].includes(p.passtyp_namn) && p.arbetstimmar > 0
            ? `${p.arbetstimmar}h` : ''
          const kommentarTxt = (p.visa_notering_i_schema && p.notering) ? p.notering : ''
          if (p.passtyp_namn && (xTimmar || kommentarTxt)) {
            harKommentar = true
            cell.value = { richText: [
              { text: p.passtyp_namn + '\n', font: { ...(cell.font || {}), size: 11 } },
              { text: [xTimmar, kommentarTxt].filter(Boolean).join('\n'), font: { ...(cell.font || {}), size: 8, bold: false } }
            ] }
          }

          // Alla ändringar för dagen som anteckning i rutan
          const hist = historikPerPass[p.id] || []
          if (hist.length > 0) {
            const text = hist.map(h =>
              `${new Date(h.andrad_at).toLocaleString('sv-SE')} (${h.andrad_av_namn}): ` +
              `${h.gammal_passtyp !== h.ny_passtyp ? `${h.gammal_passtyp} → ${h.ny_passtyp}` : h.ny_passtyp}` +
              `${h.notering ? ' – ' + h.notering : ''}`
            ).join('\n')
            cell.note = { texts: [{ text, font: { size: 9 } }], margins: { insetmode: 'custom', inset: [0.1, 0.1, 0.1, 0.1] } }
          }
        }
      })

      const summaCell = rad.getCell(dagar.length + 2)
      summaCell.font = { bold: true }
      summaCell.alignment = { horizontal: 'center', vertical: 'middle' }
      rad.height = harKommentar ? 34 : 20
    })

    // Färgnyckel längst ner
    const nyckelStartRad = medarbetare.length + 4
    ws.getCell(`A${nyckelStartRad}`).value = 'Färgnyckel:'
    ws.getCell(`A${nyckelStartRad}`).font = { bold: true }
    Object.entries(KATEGORI_FARGER).forEach(([key, farg], i) => {
      const cell = ws.getCell(`A${nyckelStartRad + 1 + i}`)
      cell.value = farg.label
      const bg = hexTillArgb(farg.bg)
      if (bg) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
      const textFarg = hexTillArgb(farg.text)
      cell.font = { color: textFarg ? { argb: textFarg } : undefined }
      if (key === 'beviljad_semester') {
        cell.value = 'Beviljad semester (grön kant)'
        const gron = { style: 'thick', color: { argb: 'FF2E7D32' } }
        cell.border = { top: gron, bottom: gron, left: gron, right: gron }
      }
    })

    const buffer = await wb.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/octet-stream' })
    const url = URL.createObjectURL(blob)
    const periodTxt = valdPeriod ? `${valdPeriod.startdatum}_${valdPeriod.slutdatum}` : new Date().toISOString().split('T')[0]
    const a = document.createElement('a')
    a.href = url
    a.download = `Planeringsunderlag_${valdAvdelning}_${periodTxt}.xlsx`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  function getPass(medarbetareId, datum) {
    return pass.find(p => p.medarbetare_id === medarbetareId && p.datum === datum)
  }

  function getTotalTimmar(medarbetareId) {
    const total = pass
      .filter(p => p.medarbetare_id === medarbetareId)
      .reduce((sum, p) => sum + (p.arbetstimmar || 0), 0)
    return Math.round(total * 10) / 10
  }

  function raknaBemanningForDag(datum) {
    const passDagen = pass.filter(p => p.datum === datum)
    const krav = hamtaBemanningskrav(valdAvdelning, datum)
    const counts = {}
    krav.forEach(k => { counts[k.key] = 0 })

    passDagen.forEach(p => {
      // Ta bort ÖT-prefix innan bemanning räknas
      const namn = (p.passtyp_namn || '').toUpperCase().replace(/^ÖT\s*/i, '').trim()
      const match = krav.find(k => k.namn.includes(namn))
      if (match) counts[match.key]++
    })

    return krav.map(k => ({
      ...k,
      har: counts[k.key] || 0,
      ok: (counts[k.key] || 0) >= k.need,
      delvis: (counts[k.key] || 0) > 0 && (counts[k.key] || 0) < k.need,
    }))
  }

  // Visa alla dagar i perioden
  const visaDagar = dagar

  if (loading) return <div className="spinner" />

  return (
    <div className="schema-page-root" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0, height: '100%' }}>
      <div className="content-header">
        <div>
          <h1>Helhetsschema – {valdAvdelning}</h1>
          <div className="content-sub">
            {dagar.length} dagar · {dagar.length > 0 && new Date(dagar[0]).toLocaleDateString('sv-SE')} –{' '}
            {dagar.length > 0 && new Date(dagar[dagar.length - 1]).toLocaleDateString('sv-SE')}
          </div>
        </div>
        <div className="btn-group">
          <select
            className="form-control"
            style={{ margin: 0, width: 'auto', fontSize: '.82rem' }}
            value={valdPeriodId || ''}
            onChange={e => setValdPeriodId(e.target.value || null)}
          >
            {(() => {
              const idag = new Date().toISOString().split('T')[0]
              const aktiva = allaPerioder
                .filter(p => p.slutdatum >= idag)
                .sort((a, b) => a.startdatum.localeCompare(b.startdatum))
              const arkiv = allaPerioder
                .filter(p => p.slutdatum < idag)
                .sort((a, b) => b.startdatum.localeCompare(a.startdatum))
              return <>
                {aktiva.map((p, i) => (
                  <option key={p.id} value={i === 0 && !valdPeriodId ? '' : p.id}>
                    {i === 0 ? '📅 ' : ''}{p.startdatum} – {p.slutdatum} ({p.antal_veckor}v)
                  </option>
                ))}
                {arkiv.length > 0 && <option disabled>── Arkiv ──</option>}
                {arkiv.map(p => (
                  <option key={p.id} value={p.id}>
                    📦 {p.startdatum} – {p.slutdatum} ({p.antal_veckor}v)
                  </option>
                ))}
              </>
            })()}
          </select>
          <select
            className="form-control"
            style={{ margin: 0, width: 140 }}
            value={valdAvdelning}
            onChange={e => { setValdAvdelning(e.target.value); setValdPeriodId(null) }}
          >
            {AVDELNINGAR.map(a => <option key={a}>{a}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={() => window.print()}>🖨 Skriv ut</button>
          <button className="btn btn-secondary btn-sm" onClick={exporteraBackup} disabled={exporterar}>
            {exporterar ? '⏳ Exporterar...' : '⬇️ Backup (Excel)'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={exporteraPlaneringsunderlag}>
            📋 Planeringsunderlag (Excel)
          </button>
        </div>
      </div>

      <div className="content-body" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, minHeight: 0 }}>
        {ansokanInfo && (() => {
          const TYPER = { ledighet: 'Ledighetsansökan', foraldraledighet: 'Föräldraledighet', passbyte: 'Passbyte', boka_dag: 'Boka dag', atf: 'ATF', flytta_pass: 'Flytta D-dag' }
          const datumTxt = (ansokanInfo.typ === 'ledighet' || ansokanInfo.typ === 'foraldraledighet')
            ? `${ansokanInfo.fran} – ${ansokanInfo.till}`
            : ansokanInfo.typ === 'flytta_pass' ? `${ansokanInfo.fran} → ${ansokanInfo.till}`
            : ansokanInfo.typ === 'boka_dag' ? ansokanInfo.fran
            : ansokanInfo.typ === 'atf' ? `${ansokanInfo.fran} · ${ansokanInfo.atfFran?.substring(0,5) || ''}–${ansokanInfo.atfTill?.substring(0,5) || ''}`
            : ''
          const rader = (ansokanInfo.kommentar || '').split(' · ').filter(Boolean)
          return (
            <div className="no-print" style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '10px 14px', marginBottom: 10, flexShrink: 0, maxHeight: '30vh', overflowY: 'auto', position: 'relative' }}>
              <button type="button" onClick={() => setAnsokanInfo(null)} title="Stäng"
                style={{ position: 'absolute', top: 6, right: 10, background: 'none', border: 'none', fontSize: '1.3rem', lineHeight: 1, cursor: 'pointer', color: '#999' }}>×</button>
              <div style={{ fontWeight: 800, fontSize: '.88rem', paddingRight: 24 }}>
                {TYPER[ansokanInfo.typ] || ansokanInfo.typ} – {ansokanInfo.namn}{ansokanInfo.avdelning ? ` (${ansokanInfo.avdelning})` : ''}
              </div>
              {datumTxt && <div style={{ fontSize: '.82rem', marginTop: 3 }}>{datumTxt}</div>}
              {rader.map((r, i) => <div key={i} style={{ fontSize: '.82rem', marginTop: 3 }}>{r}</div>)}
              {ansokanInfo.typ === 'flytta_pass' && ansokanInfo.rad?.status === 'väntar' && (
                <div style={{ fontSize: '.78rem', marginTop: 6, color: '#8a6d00' }}>Godkänner du flyttas D-dagen inte automatiskt. Flytta passet manuellt i schemat.</div>
              )}
              {ansokanInfo.rad && ansokanInfo.rad.status === 'väntar' && (
                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <textarea className="form-control" rows={1} placeholder="Kommentar till medarbetaren (valfritt)"
                    value={ansokanKommentar} onChange={e => setAnsokanKommentar(e.target.value)}
                    style={{ flex: '1 1 220px', margin: 0, fontSize: '.82rem', resize: 'vertical' }} />
                  <button className="btn btn-danger btn-sm" disabled={ansokanBusy} onClick={() => svaraFranSchema('nekad')}>✕ Neka</button>
                  <button className="btn btn-success btn-sm" disabled={ansokanBusy} onClick={() => svaraFranSchema('beviljad')}>✓ Bevilja</button>
                </div>
              )}
              {ansokanInfo.rad && ansokanInfo.rad.status !== 'väntar' && (
                <div style={{ marginTop: 8, fontSize: '.84rem', fontWeight: 700, color: ansokanInfo.rad.status === 'beviljad' ? '#2e7d32' : '#c62828' }}>
                  {ansokanInfo.rad.status === 'beviljad' ? '✓ Beviljad' : '✕ Nekad'}
                </div>
              )}
            </div>
          )
        })()}
        {/* LEGEND */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 10, flexShrink: 0 }}>
          {Object.entries(KATEGORI_FARGER).map(([key, val]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.73rem', color: '#666' }}>
              {key === 'passandring' ? (
                <span style={{fontSize:'.8rem'}}>⭐</span>
              ) : key === 'beviljad_semester' ? (
                <div style={{ position: 'relative', width: 11, height: 11, flexShrink: 0 }}>
                  <div style={{ width: 11, height: 11, borderRadius: 3, background: '#fffde7', border: '1px solid #ddd' }} />
                  <span style={{ position: 'absolute', top: -3, right: -3, fontSize: '.5rem', color: '#2e7d32', lineHeight: 1 }}>✦</span>
                </div>
              ) : key === 'beviljad_foraldraledighet' ? (
                <div style={{ position: 'relative', width: 11, height: 11, flexShrink: 0 }}>
                  <div style={{ width: 11, height: 11, borderRadius: 3, background: '#e0f7fa', border: '1px solid #ddd' }} />
                  <span style={{ position: 'absolute', top: -3, right: -3, fontSize: '.5rem', color: '#2e7d32', lineHeight: 1 }}>✦</span>
                </div>
              ) : key === 'overtid' ? (
                <div style={{ width: 11, height: 11, borderRadius: 3, background: val.bg, border: `1px solid ${val.text}`, flexShrink: 0 }} />
              ) : (
                <div style={{ 
                  width: 11, height: 11, borderRadius: 3, flexShrink: 0,
                  background: val.bg === 'transparent' ? '#fff' : val.bg,
                  border: val.bg === 'transparent' ? '1px dashed #ccc' : `1px solid ${val.text === '#ffffff' ? val.bg : '#ddd'}`
                }} />
              )}
              {val.label}
            </div>
          ))}
        </div>

        {medarbetare.length === 0 ? (
          <div className="card">
            <p style={{ color: '#aaa', textAlign: 'center' }}>Inga medarbetare hittades för {valdAvdelning}. Lägg till medarbetare eller ladda upp schema.</p>
          </div>
        ) : (
          <div className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <div ref={scrollRef} className="schema-wrap no-print" style={{ overflow: 'auto', flex: 1, scrollbarWidth: 'thin', scrollbarColor: '#F11C0A #f0f0f0' }}>
              <table className="schema-table">
                <thead>
                  <tr>
                    <th className="sticky-col">Medarbetare</th>
                    {visaDagar.map(datum => {
                      const d = new Date(datum + 'T12:00:00')
                      const isWE = arHelg(datum)
                      const helgdagar = hamtaHelgdagar(d.getFullYear())
                      const helgdag = helgdagar[datum]
                      return (
                        <th key={datum} data-datum={datum} className={isWE ? 'col-weekend' : ''} style={helgdag ? { background: '#fff5f5' } : {}}>
                          {d.getDay() === 1 && (
                            <div style={{ fontSize: '.58rem', color: '#aaa', fontWeight: 700, marginBottom: 1 }}>
                              v.{(() => { const d2 = new Date(datum+'T12:00:00'); d2.setDate(d2.getDate()+3-(d2.getDay()+6)%7); const w1=new Date(d2.getFullYear(),0,4); return 1+Math.round(((d2-w1)/86400000-3+(w1.getDay()+6)%7)/7) })()}
                            </div>
                          )}
                          <span style={{ fontWeight: (isWE || helgdag) ? 700 : undefined }}>
                            {d.toLocaleDateString('sv-SE', { weekday: 'short' })}
                          </span><br />
                          {d.getDate()}/{d.getMonth() + 1}
                          {helgdag && <div style={{ fontSize: '.62rem', color: '#c62828', fontStyle: 'italic', fontWeight: 400 }}>{helgdag}</div>}
                        </th>
                      )
                    })}
                    <th style={{ background: '#fff8f0', color: '#bf5000', fontSize: '.68rem' }}>Total</th>
                  </tr>
                  {/* Noteringsrad */}
                  <tr style={{ background: '#fafff8' }}>
                    <td className="sticky-col" style={{ fontSize: '.68rem', color: '#888', fontWeight: 600, background: '#fafff8', padding: '3px 8px' }}>📌 Noteringar</td>
                    {dagar.map(datum => (
                      <td key={datum} style={{ textAlign: 'center', padding: '2px', cursor: 'pointer', minWidth: 44 }}
                        onClick={() => setRedigeraDagNotis(datum)}>
                        {dagnoteringar[datum] ? (
                          <span title={dagnoteringar[datum]} style={{ fontSize: '.6rem', background: '#e8f5e9', color: '#2e7d32', borderRadius: 3, padding: '1px 4px', display: 'inline-block', maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {dagnoteringar[datum]}
                          </span>
                        ) : (
                          <span style={{ color: '#ddd', fontSize: '.6rem' }}>+</span>
                        )}
                      </td>
                    ))}
                    <td />
                  </tr>
                </thead>

                <tbody>
                  {medarbetare.map(med => {
                    const totalH = getTotalTimmar(med.id)
                    return (
                      <tr key={med.id}>
                        <td className="sticky-col">{med.fornamn} {med.efternamn}</td>
                        {visaDagar.map(datum => {
                          const p = getPass(med.id, datum)
                          const isWE = arHelg(datum)
                          const farger = p ? KATEGORI_FARGER[p.kategori] : null

                          return (
                            <td key={datum} className={isWE ? 'col-weekend' : ''}>
                              {p ? (
                                <span
                                  className="pass-cell"
                                  style={{ background: farger?.bg, color: farger?.text, position: 'relative', minWidth: p.passtyp_namn ? undefined : 24, display: 'inline-block', outline: markerade.find(m => m.id === p.id) ? '2px solid #1565c0' : 'none', outlineOffset: 1 }}
                                  onClick={(e) => {
                                    if (e.ctrlKey || e.metaKey) {
                                      // Ctrl+klick = markera/avmarkera
                                      setMarkerade(prev => {
                                        const finns = prev.find(m => m.id === p.id)
                                        if (finns) return prev.filter(m => m.id !== p.id)
                                        return [...prev, { ...p, medarbetare_namn: `${med.fornamn} ${med.efternamn}` }]
                                      })
                                    } else {
                                      setMarkerade([])
                                      setValtPass({ ...p, medarbetare_namn: `${med.fornamn} ${med.efternamn}` })
                                    }
                                  }}
                                >
                                  {p.passtyp_namn || '   '}
                                  {['X','SX','LX','SJX'].includes((p.passtyp_namn||'').toUpperCase()) && p.arbetstimmar !== null && p.arbetstimmar !== undefined && (
                                    <div style={{fontSize:'.58rem',opacity:.75,lineHeight:1,marginTop:1}}>{p.arbetstimmar}h</div>
                                  )}
                                  {(p.har_atf || p.kategori === 'permission' || p.kategori === 'flex') && (
                                    <div style={{fontSize:'.55rem',fontWeight:700,lineHeight:1,marginTop:1,opacity:.9}}>
                                      {p.har_atf
                                        ? `ATF ${p.atf_fran?.substring(0,5) || ''}-${p.atf_till?.substring(0,5) || ''}`
                                        : p.kategori === 'permission' ? 'Perm' : 'FLEX'}
                                    </div>
                                  )}
                                  {(p.har_beredskap || p.har_boka_dag) && (
                                    <div style={{height:5,borderRadius:2,marginTop:2,background: p.har_beredskap ? '#e74c3c' : '#3498db'}} title={p.har_beredskap ? 'Beredskap' : 'Bokad dag'} />
                                  )}
                                  {p.visa_notering_i_schema && p.notering && (
                                    <div style={{fontSize:'.5rem',fontWeight:700,lineHeight:1.1,marginTop:1,opacity:.9,maxWidth:44,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={p.notering}>
                                      {p.notering}
                                    </div>
                                  )}

                                  {(p.kategori === 'passandring' || p.har_passandring) && (
                                    <span style={{position:'absolute',top:-3,right:-3,fontSize:'.5rem',lineHeight:1,pointerEvents:'none'}}>⭐</span>
                                  )}
                                  {p.kategori === 'beviljad_semester' && (
                                    <span style={{position:'absolute',top:-3,right:-3,fontSize:'.45rem',lineHeight:1,color:'#2e7d32',pointerEvents:'none'}}>✦</span>
                                  )}
                                  {p.kategori === 'beviljad_foraldraledighet' && (
                                    <span style={{position:'absolute',top:-3,right:-3,fontSize:'.45rem',lineHeight:1,color:'#2e7d32',pointerEvents:'none'}}>✦</span>
                                  )}

                                </span>
                              ) : (
                                <span
                                  style={{ 
                                    color: markerade.find(m => m.datum === datum && m.medarbetare_id === med.id) ? '#1565c0' : '#ddd', 
                                    cursor: 'pointer', fontSize: '.76rem',
                                    outline: markerade.find(m => m.datum === datum && m.medarbetare_id === med.id) ? '2px solid #1565c0' : 'none',
                                    borderRadius: 3, padding: '1px 3px'
                                  }}
                                  onClick={(e) => {
                                    const tomtPass = { id: null, datum, medarbetare_id: med.id, medarbetare_namn: `${med.fornamn} ${med.efternamn}`, passtyp_id: null, passtyp_namn: null, kategori: 'ordinarie' }
                                    if (e.ctrlKey || e.metaKey) {
                                      setMarkerade(prev => {
                                        const finns = prev.find(m => m.datum === datum && m.medarbetare_id === med.id)
                                        if (finns) return prev.filter(m => !(m.datum === datum && m.medarbetare_id === med.id))
                                        return [...prev, tomtPass]
                                      })
                                    } else {
                                      setMarkerade([])
                                      setValtPass(tomtPass)
                                    }
                                  }}
                                >—</span>
                              )}
                            </td>
                          )
                        })}
                        {(() => {
                          const krav = valdPeriod ? valdPeriod.antal_veckor * 34.5 : 345
                          const ok = Math.abs(totalH - krav) <= 0.05
                          const low = totalH < krav - 1
                          return (
                            <td className={ok ? 'sum-ok' : low ? 'sum-low' : ''}>
                              {totalH.toFixed(1)}h
                            </td>
                          )
                        })()}
                      </tr>
                    )
                  })}
                </tbody>

                {/* BEMANNING-RAD */}
                <tfoot>
                  <tr>
                    <td className="sticky-col" style={{ fontSize: '.75rem', fontWeight: 700, color: '#555', verticalAlign: 'top', paddingTop: 8 }}>
                      Bemanning
                    </td>
                    {visaDagar.map(datum => {
                      const bemanning = raknaBemanningForDag(datum)
                      const isWE = arHelg(datum)
                      return (
                        <td key={datum} className={`bem-cell${isWE ? ' col-weekend' : ''}`}>
                          {bemanning.map(b => (
                            <div
                              key={b.key}
                              className={`bem-row ${b.ok ? 'bem-ok' : b.delvis ? 'bem-warn' : 'bem-missing'}`}
                            >
                              <span style={{ fontWeight: 600 }}>{b.label}</span>
                              <span style={{ fontWeight: 700 }}>
                                {b.har}/{b.need} {b.ok ? '✓' : b.delvis ? '⚡' : '🚨'}
                              </span>
                            </div>
                          ))}
                        </td>
                      )
                    })}
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Utskriftsanpassad version: samma data, men uppdelad i smalare
                block om ~10 dagar i taget, så inget klipps bort i sidled
                när det skrivs ut - visas ALDRIG på skärmen. */}
            <div className="print-only" style={{ display: 'none' }}>
              {(() => {
                const DAGAR_PER_BLOCK = 10
                const block = []
                for (let i = 0; i < visaDagar.length; i += DAGAR_PER_BLOCK) {
                  block.push(visaDagar.slice(i, i + DAGAR_PER_BLOCK))
                }
                return block.map((dagBlock, bi) => (
                  <table key={bi} className="schema-table print-block" style={{ pageBreakAfter: bi < block.length - 1 ? 'always' : 'auto', width: '100%', marginBottom: 16 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', minWidth: 110 }}>Medarbetare</th>
                        {dagBlock.map(datum => {
                          const d = new Date(datum + 'T12:00:00')
                          const isWE = arHelg(datum)
                          return (
                            <th key={datum} style={isWE ? { background: '#fff5f5' } : {}}>
                              <span style={{ fontWeight: isWE ? 700 : undefined }}>{d.toLocaleDateString('sv-SE', { weekday: 'short' })}</span><br />
                              {d.getDate()}/{d.getMonth() + 1}
                            </th>
                          )
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {medarbetare.map(med => (
                        <tr key={med.id}>
                          <td>{med.fornamn} {med.efternamn}</td>
                          {dagBlock.map(datum => {
                            const p = getPass(med.id, datum)
                            const farger = p ? KATEGORI_FARGER[p.kategori] : null
                            return (
                              <td key={datum} style={{ textAlign: 'center', background: farger?.bg, color: farger?.text, position: 'relative' }}>
                                {p ? (
                                  <>
                                    {(p.kategori === 'passandring' || p.har_passandring) && (
                                      <span style={{ position: 'absolute', top: 0, right: 1, fontSize: '.7em' }}>⭐</span>
                                    )}
                                    {p.passtyp_namn || ''}
                                    {['X','SX','LX','SJX'].includes((p.passtyp_namn||'').toUpperCase()) && p.arbetstimmar != null && (
                                      <div style={{fontSize:'.6em',opacity:.75}}>{p.arbetstimmar}h</div>
                                    )}
                                    {(p.har_atf || p.kategori === 'permission' || p.kategori === 'flex') && (
                                      <div style={{fontSize:'.6em',fontWeight:700,opacity:.9}}>
                                        {p.har_atf ? `ATF ${p.atf_fran?.substring(0,5) || ''}-${p.atf_till?.substring(0,5) || ''}` : p.kategori === 'permission' ? 'Perm' : 'FLEX'}
                                      </div>
                                    )}
                                    {(p.har_beredskap || p.har_boka_dag) && (
                                      <div style={{marginTop:2,height:4,borderTop: `3px solid ${p.har_beredskap ? '#e74c3c' : '#3498db'}`}} />
                                    )}
                                    {p.visa_notering_i_schema && p.notering && (
                                      <div style={{fontSize:'.55em',fontWeight:700,opacity:.9,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.notering}</div>
                                    )}
                                  </>
                                ) : '–'}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ))
              })()}
            </div>
          </div>
        )}

        <div style={{ fontSize: '.74rem', color: '#bbb', marginTop: 6, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>⭐ = passändring · ✦ = beviljad semester · Ctrl+klick för att markera flera</span>
          {markerade.length > 0 && (
            <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ background: '#1565c0', color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: '.72rem', fontWeight: 700 }}>
                {markerade.length} markerade
              </span>
              <button
                className="btn btn-primary btn-sm"
                style={{ fontSize: '.72rem' }}
                onClick={() => setValtPass({ ...markerade[0], medarbetare_namn: `${markerade.length} markerade pass`, _multimarkerade: markerade })}
              >
                ✏️ Redigera alla
              </button>
              <button
                className="btn btn-secondary btn-sm"
                style={{ fontSize: '.72rem' }}
                onClick={() => setMarkerade([])}
              >
                ✕ Avmarkera
              </button>
            </span>
          )}
        </div>
      </div>

      {/* REDIGERA PASS MODAL */}
      {/* Dagnotering modal */}
      {redigeraDagNotis && (
        <div
          className="modal-overlay"
          onMouseDown={e => { notisOverlayMouseDownPaOverlay.current = (e.target === e.currentTarget) }}
          onClick={e => { if (e.target === e.currentTarget && notisOverlayMouseDownPaOverlay.current) setRedigeraDagNotis(null) }}
        >
          <div className="modal-box" style={{ maxWidth: 340 }}>
            <div className="modal-header">
              <h3>📌 Notering – {new Date(redigeraDagNotis + 'T12:00:00').toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
              <button className="modal-close" onClick={() => setRedigeraDagNotis(null)}>×</button>
            </div>
            <div className="modal-body">
              <textarea
                className="form-control"
                rows={3}
                defaultValue={dagnoteringar[redigeraDagNotis] || ''}
                placeholder="Skriv notering... (lämna tomt för att ta bort)"
                id="dagnotis-input"
                autoFocus
              />
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setRedigeraDagNotis(null)}>Avbryt</button>
              <button className="btn btn-primary" onClick={() => sparaDagNotis(redigeraDagNotis, document.getElementById('dagnotis-input').value)}>💾 Spara</button>
            </div>
          </div>
        </div>
      )}

      {valtPass && (
        <RedigeraPassModal
          pass={valtPass}
          passtyper={passtyper}
          avdelning={valdAvdelning}
          allaMedarbetarePass={pass}
          onSave={async (uppdateratPass) => {
            const pos = scrollRef.current?.scrollLeft || 0
            setValtPass(null)
            setMarkerade([]) // Rensa markeringar efter sparning

            // Ladda om pass-data
            const medIds = medarbetare.map(m => m.id)
            const nyaPass = await hamtaAllaSidor(() => supabase
              .from('pass')
              .select('*, passtyp:passtyper(*), har_passandring')
              .in('medarbetare_id', medIds)
              .gte('datum', dagar[0])
              .lte('datum', dagar[dagar.length - 1])
            )
            setPass(nyaPass || [])
            // Återställ scroll
            requestAnimationFrame(() => {
              if (scrollRef.current) scrollRef.current.scrollLeft = pos
            })
          }}
          onClose={() => setValtPass(null)}
        />
      )}
    </div>
  )
}
